import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '../utils/toastUtils';
import { dynamicOrchestrator } from '../api/client';
import {
  Play, Square, RefreshCw, Cpu, Database, HardDrive, Wifi,
  CheckCircle, XCircle, Clock, Loader2, ChevronDown, ChevronUp,
  Target, Zap, Activity, BarChart3, Settings2, AlertTriangle
} from 'lucide-react';

// Traductions des phases de pentest
const PHASE_LABELS = {
  RECONNAISSANCE: 'Reconnaissance',
  ENUMERATION: 'Énumération',
  VULNERABILITY_SCANNING: 'Scan de vulnérabilités',
  EXPLOITATION: 'Exploitation',
  POST_EXPLOITATION: 'Post-Exploitation',
  REPORTING: 'Rapport',
};

// Couleurs du thème ROUGE Attack
const COLORS = {
  primary: '#f44336',
  primaryDark: '#d32f2f',
  primaryLight: '#e57373',
  success: '#4caf50',
  warning: '#ff9800',
  danger: '#b71c1c',
  background: '#1a0000',
  surface: '#2d0a0a',
  surfaceLight: '#3d1a1a',
  text: '#ffffff',
  textSecondary: '#ffcdd2',
  border: 'rgba(244, 67, 54, 0.3)',
};

// Composant de jauge circulaire
const CircularGauge = ({ value, max = 100, label, color, icon: Icon, unit = '%' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={COLORS.surfaceLight}
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {Icon && <Icon className="w-4 h-4 mb-1" style={{ color }} />}
          <span className="text-lg font-bold" style={{ color }}>{value.toFixed(1)}{unit}</span>
        </div>
      </div>
      <span className="text-sm text-gray-400 mt-2">{label}</span>
    </div>
  );
};

// Helper function to get gradient color based on percentage
const getGradientColor = (percentage) => {
  // Smooth gradient: green (0%) -> yellow (50%) -> red (100%)
  if (percentage <= 50) {
    // Green to Yellow transition (0-50%)
    const ratio = percentage / 50;
    const r = Math.round(76 + (255 - 76) * ratio);  // 76 (green) to 255 (yellow)
    const g = Math.round(175 + (152 - 175) * ratio); // 175 (green) to 152 (yellow/orange)
    const b = Math.round(80 - 80 * ratio);           // 80 (green) to 0 (yellow)
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to Red transition (50-100%)
    const ratio = (percentage - 50) / 50;
    const r = Math.round(255 - (255 - 244) * ratio); // 255 (yellow) to 244 (red)
    const g = Math.round(152 - 152 * ratio);          // 152 (yellow) to 0 (red)
    const b = Math.round(0 + 67 * ratio);             // 0 (yellow) to 67 (red)
    return `rgb(${r}, ${g}, ${b})`;
  }
};

// Composant de barre de progression
const ProgressBar = ({ value, max = 100, color, label, useGradient = false }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const barColor = useGradient ? getGradientColor(percentage) : color;
  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between mb-1">
          <span className="text-xs text-gray-400">{label}</span>
          <span className="text-xs font-mono" style={{ color: barColor }}>{value.toFixed(1)}%</span>
        </div>
      )}
      <div className="h-2 rounded-full" style={{ backgroundColor: COLORS.surfaceLight }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: barColor }}
        />
      </div>
    </div>
  );
};

// Composant de carte d'outil
const ToolCard = ({ tool, onClick }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return COLORS.success;
      case 'running': return COLORS.warning;
      case 'failed': return COLORS.danger;
      case 'pending': return COLORS.textSecondary;
      default: return COLORS.textSecondary;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'running': return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const statusColor = getStatusColor(tool.status);

  return (
    <div
      className="p-3 rounded-lg border cursor-pointer transition-all hover:scale-105"
      style={{
        backgroundColor: COLORS.surface,
        borderColor: statusColor,
        boxShadow: tool.status === 'running' ? `0 0 10px ${statusColor}` : 'none'
      }}
      onClick={() => onClick?.(tool)}
    >
      <div className="flex items-center justify-between mb-2 min-w-0">
        <span className="font-medium text-sm text-white truncate" title={tool.name}>{tool.name}</span>
        <div className="flex-shrink-0 ml-2" style={{ color: statusColor }}>{getStatusIcon(tool.status)}</div>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <Cpu className="w-3 h-3" />
        <span>{tool.cpu_weight}</span>
        <Database className="w-3 h-3 ml-2" />
        <span>{tool.ram_gb}GB</span>
      </div>
      {!tool.installed && (
        <div className="mt-2 flex items-center gap-1 text-xs text-yellow-500">
          <AlertTriangle className="w-3 h-3" />
          <span>Non installé</span>
        </div>
      )}
    </div>
  );
};

// Composant principal
const DynamicOrchestratorMonitor = ({ onPentestStart, onPentestComplete }) => {
  // États
  const [status, setStatus] = useState(null);
  const [resources, setResources] = useState(null);
  const [tools, setTools] = useState(null);
  const [config, setConfig] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);

  // Formulaire de démarrage
  const [target, setTarget] = useState('');
  const [scanProfile, setScanProfile] = useState('standard');
  const [showStartForm, setShowStartForm] = useState(false);

  // Polling interval
  const pollIntervalRef = useRef(null);

  // Charger les données initiales
  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [statusData, resourcesData, toolsData, configData, statsData] = await Promise.all([
        dynamicOrchestrator.getStatus().catch(() => null),
        dynamicOrchestrator.getResources().catch(() => null),
        dynamicOrchestrator.getTools().catch(() => null),
        dynamicOrchestrator.getConfig().catch(() => null),
        dynamicOrchestrator.getStatistics().catch(() => null),
      ]);

      // Check if we got any data at all
      const hasAnyData = statusData || resourcesData || toolsData || configData || statsData;

      if (statusData) setStatus(statusData);
      if (resourcesData) setResources(resourcesData);
      if (toolsData) setTools(toolsData);
      if (configData) setConfig(configData);
      if (statsData) setStatistics(statsData);

      if (hasAnyData) {
        setHasConnectedOnce(true);
      }

      setIsRunning(statusData?.status === 'running');
      setLoading(false);
    } catch (err) {
      console.error('Error loading orchestrator data:', err);
      // Only show error if we previously connected successfully (avoid error on first load)
      if (hasConnectedOnce) {
        setError('Erreur de connexion à l\'orchestrateur');
      }
      setLoading(false);
    }
  }, [hasConnectedOnce]);

  // Polling pendant l'exécution
  useEffect(() => {
    loadData();

    // Polling plus fréquent si un pentest est en cours
    const pollInterval = isRunning ? 2000 : 10000;
    pollIntervalRef.current = setInterval(loadData, pollInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadData, isRunning]);

  // Démarrer un pentest
  const handleStartPentest = async () => {
    if (!target.trim()) {
      toast.error('Veuillez entrer une cible');
      return;
    }

    try {
      const result = await dynamicOrchestrator.start(target, scanProfile);
      toast.success(`Pentest démarré: ${result.pentest_id}`);
      setIsRunning(true);
      setShowStartForm(false);
      onPentestStart?.(result);
      loadData();
    } catch (err) {
      console.error('Error starting pentest:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors du démarrage');
    }
  };

  // Arrêter le pentest
  const handleStopPentest = async () => {
    try {
      await dynamicOrchestrator.stop();
      toast.success('Pentest arrêté');
      setIsRunning(false);
      onPentestComplete?.();
      loadData();
    } catch (err) {
      console.error('Error stopping pentest:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'arrêt');
    }
  };

  // Exécuter un outil unique
  const handleExecuteTool = async (tool) => {
    if (!target.trim()) {
      toast.error('Veuillez d\'abord configurer une cible');
      setShowStartForm(true);
      return;
    }

    try {
      toast.info(`Exécution de ${tool.name}...`);
      const result = await dynamicOrchestrator.executeTool(tool.name, target);
      toast.success(`${tool.name} terminé`);
      loadData();
    } catch (err) {
      console.error('Error executing tool:', err);
      toast.error(err.response?.data?.detail || `Erreur: ${tool.name}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.primary }} />
        <span className="ml-3 text-gray-400">Chargement de l'orchestrateur...</span>
      </div>
    );
  }

  // Show error banner only if we previously connected (connection lost scenario)
  // Uses amber/yellow colors to indicate a connection issue (not a critical error)
  if (error && hasConnectedOnce) {
    return (
      <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-900/20">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <span className="text-amber-200">{error}</span>
          <button
            onClick={loadData}
            className="ml-auto px-3 py-1.5 rounded text-sm bg-amber-600 hover:bg-amber-500 text-white transition-colors flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Show a gentle message if orchestrator is not available on initial load
  // Uses neutral colors to avoid being mistaken for an error banner
  if (!hasConnectedOnce && !loading && !status && !tools) {
    return (
      <div className="p-6 rounded-xl border border-gray-700 bg-gray-800/50">
        <div className="flex flex-col items-center justify-center text-center py-8">
          <div className="w-12 h-12 mb-4 rounded-full bg-gray-700/50 flex items-center justify-center">
            <Zap className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Orchestrateur en attente</h3>
          <p className="text-gray-400 mb-4 max-w-md">
            L'orchestrateur de pentest dynamique n'est pas disponible actuellement.
            Cliquez sur le bouton ci-dessous pour réessayer la connexion.
          </p>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 bg-gray-700 hover:bg-gray-600 text-white"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Réessayer</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec contrôles */}
      <div className="p-4 rounded-xl border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surface }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6" style={{ color: COLORS.primary }} />
            <div>
              <h2 className="text-lg font-bold text-white">Orchestrateur du Pentest Dynamique</h2>
              <p className="text-xs text-gray-400">Orchestration intelligente avec parallélisme adaptatif</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isRunning ? (
              <button
                onClick={() => setShowStartForm(!showStartForm)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                style={{ backgroundColor: COLORS.primary }}
              >
                <Play className="w-4 h-4" />
                <span>Démarrer</span>
              </button>
            ) : (
              <button
                onClick={handleStopPentest}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                style={{ backgroundColor: COLORS.danger }}
              >
                <Square className="w-4 h-4" />
                <span>Arrêter</span>
              </button>
            )}
            <button
              onClick={loadData}
              className="p-2 rounded-lg border transition-all hover:bg-white/10"
              style={{ borderColor: COLORS.border }}
            >
              <RefreshCw className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Formulaire de démarrage */}
        {showStartForm && !isRunning && (
          <div className="mt-4 p-4 rounded-lg border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surfaceLight }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: COLORS.textSecondary }}>Cible</label>
                <input
                  type="text"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="192.168.1.100 ou domain.com"
                  className="w-full px-3 py-2 rounded-lg bg-black/30 border text-white placeholder-gray-400 focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.border, '--tw-ring-color': COLORS.primary }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: COLORS.textSecondary }}>Profil de scan</label>
                <select
                  value={scanProfile}
                  onChange={(e) => setScanProfile(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-black/30 border text-white focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.border }}
                >
                  <option value="quick">Rapide</option>
                  <option value="standard">Standard</option>
                  <option value="aggressive">Agressif (Complet)</option>
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <label className="block text-sm text-transparent mb-1 select-none" aria-hidden="true">&nbsp;</label>
                <button
                  onClick={handleStartPentest}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium"
                  style={{ backgroundColor: COLORS.success }}
                >
                  <Target className="w-4 h-4" />
                  <span>Lancer le Pentest</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statut du pentest en cours */}
        {status && isRunning && (
          <div className="mt-4 p-4 rounded-lg border" style={{ borderColor: COLORS.warning, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: COLORS.warning }} />
                <div>
                  <span className="font-medium text-white">Pentest en cours</span>
                  <span className="text-sm text-gray-400 ml-2">ID: {status.pentest_id}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: COLORS.primary }}>{status.progress}%</div>
                <div className="text-xs text-gray-400">{status.current_phase}</div>
              </div>
            </div>
            <div className="mt-3">
              <ProgressBar value={status.progress} color={COLORS.primary} />
            </div>
            <div className="mt-3 flex gap-4 text-sm">
              <span className="text-green-400">{status.completed_tools?.length || 0} terminés</span>
              <span className="text-red-400">{status.failed_tools?.length || 0} échecs</span>
              <span className="text-yellow-400">{status.running_tasks?.length || 0} en cours</span>
            </div>
          </div>
        )}
      </div>

      {/* Ressources système */}
      {resources && (
        <div className="p-4 rounded-xl border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surface }}>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5" style={{ color: COLORS.primary }} />
            <h3 className="text-lg font-bold text-white">Ressources Système</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <CircularGauge
              value={resources.cpu?.used_percent || 0}
              label="CPU"
              color={resources.cpu?.used_percent > 80 ? COLORS.danger : resources.cpu?.used_percent > 50 ? COLORS.warning : COLORS.success}
              icon={Cpu}
            />
            <CircularGauge
              value={resources.ram?.used_percent || 0}
              label="RAM"
              color={resources.ram?.used_percent > 80 ? COLORS.danger : resources.ram?.used_percent > 50 ? COLORS.warning : COLORS.success}
              icon={Database}
            />
            <CircularGauge
              value={resources.gpu?.available_percent || 0}
              label="GPU"
              color={COLORS.primary}
              icon={HardDrive}
            />
            <CircularGauge
              value={resources.active_tasks || 0}
              max={10}
              label="Tâches actives"
              color={COLORS.warning}
              icon={Wifi}
              unit=""
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-2 rounded bg-black/20 min-w-0" title="Cœurs CPU">
              <span className="text-gray-400 truncate">Cœurs CPU :</span>
              <span className="ml-2 text-white font-mono">{resources.cpu?.total_cores || 0}</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="RAM totale">
              <span className="text-gray-400 truncate">RAM totale :</span>
              <span className="ml-2 text-white font-mono">{resources.ram?.total_gb?.toFixed(1) || 0} Go</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="VRAM GPU">
              <span className="text-gray-400 truncate">VRAM GPU :</span>
              <span className="ml-2 text-white font-mono">{resources.gpu?.total_vram_gb?.toFixed(1) || 0} Go</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="Disque libre">
              <span className="text-gray-400 truncate">Disque libre :</span>
              <span className="ml-2 text-white font-mono">{resources.disk?.free_gb?.toFixed(1) || 0} Go</span>
            </div>
          </div>
        </div>
      )}

      {/* Outils par phase */}
      {tools && (
        <div className="p-4 rounded-xl border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surface }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" style={{ color: COLORS.primary }} />
              <h3 className="text-lg font-bold text-white">Outils de Pentest</h3>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-400">{tools.installed_tools} installés</span>
              <span className="text-gray-400">/ {tools.total_tools} total</span>
            </div>
          </div>

          {tools.by_phase && Object.entries(tools.by_phase).map(([phase, phaseTools]) => {
            const completedCount = phaseTools.filter(t => t.status === 'completed').length;
            const totalCount = phaseTools.length;
            const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

            return (
            <div key={phase} className="mb-3">
              <button
                onClick={() => setExpandedPhase(expandedPhase === phase ? null : phase)}
                className="w-full flex items-center justify-between p-3 rounded-lg border transition-all hover:bg-white/5"
                style={{ borderColor: COLORS.border }}
                title={PHASE_LABELS[phase] || phase}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-white font-medium truncate">{PHASE_LABELS[phase] || phase}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.surfaceLight }}>
                    {phaseTools.length} outils
                  </span>
                  {/* Progress bar with percentage label */}
                  <div className="flex items-center gap-2 flex-1 max-w-[200px]">
                    <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: COLORS.surfaceLight }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${progressPercent}%`,
                          backgroundColor: progressPercent === 100 ? COLORS.success : progressPercent > 0 ? COLORS.warning : COLORS.textSecondary
                        }}
                      />
                    </div>
                    <span className="text-xs font-mono flex-shrink-0" style={{
                      color: progressPercent === 100 ? COLORS.success : progressPercent > 0 ? COLORS.warning : COLORS.textSecondary,
                      minWidth: '45px',
                      textAlign: 'right'
                    }}>
                      {progressPercent.toFixed(0)}%
                    </span>
                  </div>
                </div>
                {expandedPhase === phase ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              </button>

              {expandedPhase === phase && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-3">
                  {phaseTools.map((tool) => (
                    <ToolCard
                      key={tool.name}
                      tool={tool}
                      onClick={handleExecuteTool}
                    />
                  ))}
                </div>
              )}
            </div>
          );})}
        </div>
      )}

      {/* Statistiques */}
      {statistics && (
        <div className="p-4 rounded-xl border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surface }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5" style={{ color: COLORS.primary }} />
            <h3 className="text-lg font-bold text-white">Statistiques</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg min-w-0" style={{ backgroundColor: COLORS.surfaceLight }}>
              <div className="text-2xl font-bold truncate" style={{ color: COLORS.success }}>{statistics.total_tasks_executed}</div>
              <div className="text-xs text-gray-400 truncate" title="Tâches exécutées">Tâches exécutées</div>
            </div>
            <div className="p-3 rounded-lg min-w-0" style={{ backgroundColor: COLORS.surfaceLight }}>
              <div className="text-2xl font-bold truncate" style={{ color: COLORS.danger }}>{statistics.failed_tasks}</div>
              <div className="text-xs text-gray-400 truncate" title="Échecs">Échecs</div>
            </div>
            <div className="p-3 rounded-lg min-w-0" style={{ backgroundColor: COLORS.surfaceLight }}>
              <div className="text-2xl font-bold truncate" style={{ color: COLORS.warning }}>{statistics.current_active_tasks}</div>
              <div className="text-xs text-gray-400 truncate" title="Tâches actives">Tâches actives</div>
            </div>
            <div className="p-3 rounded-lg min-w-0" style={{ backgroundColor: COLORS.surfaceLight }}>
              <div className="text-2xl font-bold truncate" style={{ color: COLORS.primary }}>{statistics.average_task_duration_sec}s</div>
              <div className="text-xs text-gray-400 truncate" title="Durée moyenne">Durée moyenne</div>
            </div>
          </div>

          {statistics.peak_usage && (
            <div className="mt-4">
              <h4 className="text-sm text-gray-400 mb-2">Pics d'utilisation</h4>
              <div className="grid grid-cols-3 gap-4">
                <ProgressBar
                  value={statistics.peak_usage.cpu || 0}
                  label="CPU Max"
                  useGradient={true}
                />
                <ProgressBar
                  value={statistics.peak_usage.ram || 0}
                  label="RAM Max"
                  useGradient={true}
                />
                <ProgressBar
                  value={statistics.peak_usage.gpu || 0}
                  label="GPU Max"
                  useGradient={true}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Configuration */}
      {config && (
        <div className="p-4 rounded-xl border" style={{ borderColor: COLORS.border, backgroundColor: COLORS.surface }}>
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="w-5 h-5" style={{ color: COLORS.primary }} />
            <h3 className="text-lg font-bold text-white">Configuration</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="p-2 rounded bg-black/20 min-w-0" title="CPU max">
              <span className="text-gray-400 truncate">CPU max :</span>
              <span className="ml-2 text-white font-mono">{config.resource_limits?.max_cpu_percent}%</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="RAM max">
              <span className="text-gray-400 truncate">RAM max :</span>
              <span className="ml-2 text-white font-mono">{config.resource_limits?.max_ram_percent}%</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="GPU max">
              <span className="text-gray-400 truncate">GPU max :</span>
              <span className="ml-2 text-white font-mono">{config.resource_limits?.max_gpu_percent}%</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="Pool de threads">
              <span className="text-gray-400 truncate">Pool de threads :</span>
              <span className="ml-2 text-white font-mono">{config.executor_config?.thread_pool_workers}</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="Pool de processus">
              <span className="text-gray-400 truncate">Pool de processus :</span>
              <span className="ml-2 text-white font-mono">{config.executor_config?.process_pool_workers}</span>
            </div>
            <div className="p-2 rounded bg-black/20 min-w-0" title="Outils installés">
              <span className="text-gray-400 truncate">Outils installés :</span>
              <span className="ml-2 text-white font-mono">{Math.min(config.installed_tools_count || 0, config.tool_count || 0)}/{config.tool_count || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicOrchestratorMonitor;
